import { useState, useEffect } from "react"
import { supabase } from "@/integrations/supabase/client"

interface ContentItem {
  type: "image" | "video" | "music"
  id: string
}

interface LikeData {
  likeCount: number
  isLiked: boolean
}

export function useBatchLikeCounts(contentItems: ContentItem[]) {
  const [likeData, setLikeData] = useState<Record<string, LikeData>>({})
  const [loading, setLoading] = useState(true)
  const [userId, setUserId] = useState<string | null>(null)

  useEffect(() => {
    if (contentItems.length === 0) {
      setLoading(false)
      return
    }

    fetchBatchLikeData()
  }, [JSON.stringify(contentItems)])

  const fetchBatchLikeData = async () => {
    setLoading(true)
    try {
      // Get current user
      const { data: { session } } = await supabase.auth.getSession()
      setUserId(session?.user?.id || null)

      // Prepare content items for batch query
      const itemsJson = contentItems.map(item => ({
        type: item.type,
        id: item.id
      }))

      // Batch fetch like counts (single query instead of N queries)
      const { data: likeCounts, error: countError } = await supabase
        .rpc("get_batch_like_counts", { content_items: itemsJson })

      if (countError) {
        console.error("Error fetching batch like counts:", countError)
      }

      // Create a map of like counts
      const countsMap: Record<string, number> = {}
      likeCounts?.forEach((item: { content_type: string; content_id: string; like_count: number }) => {
        countsMap[`${item.content_type}-${item.content_id}`] = item.like_count
      })

      // Batch fetch user's likes (single query instead of N queries)
      let userLikesSet = new Set<string>()
      if (session?.user?.id) {
        const { data: userLikes, error: likesError } = await supabase
          .rpc("get_user_likes_batch", { 
            user_id_param: session.user.id,
            content_items: itemsJson 
          })

        if (likesError) {
          console.error("Error fetching user likes:", likesError)
        }

        userLikes?.forEach((item: { content_type: string; content_id: string }) => {
          userLikesSet.add(`${item.content_type}-${item.content_id}`)
        })
      }

      // Combine into final data structure
      const newLikeData: Record<string, LikeData> = {}
      contentItems.forEach(item => {
        const key = `${item.type}-${item.id}`
        newLikeData[key] = {
          likeCount: countsMap[key] || 0,
          isLiked: userLikesSet.has(key)
        }
      })

      setLikeData(newLikeData)
    } catch (error) {
      console.error("Error in batch like fetch:", error)
    } finally {
      setLoading(false)
    }
  }

  const updateLikeData = (contentType: string, contentId: string, isLiked: boolean, delta: number) => {
    const key = `${contentType}-${contentId}`
    setLikeData(prev => ({
      ...prev,
      [key]: {
        likeCount: Math.max(0, (prev[key]?.likeCount || 0) + delta),
        isLiked
      }
    }))
  }

  const getLikeData = (contentType: string, contentId: string): LikeData => {
    const key = `${contentType}-${contentId}`
    return likeData[key] || { likeCount: 0, isLiked: false }
  }

  return {
    likeData,
    loading,
    userId,
    getLikeData,
    updateLikeData,
    refetch: fetchBatchLikeData
  }
}
